/**
 * StartDateField
 *
 * Shared "Starts" date picker used by all 3 program-assignment dialogs:
 *   - AssignProgramDialog (single client, from client profile)
 *   - AssignAfterSaveDialog (new program → assign now flow)
 *   - BulkAssignProgramDialog (one program → many clients)
 *
 * Design (trust-layer item #2):
 *   LABEL:     Starts
 *   INPUT:     [popover-calendar — defaults to next Monday via getDefaultStartDate]
 *   HINT:      We'll count Day 1 from this date.
 *
 * Required field. If the trainer ever clears the calendar selection, the
 * parent should re-seed to the default — never submit blank.
 */

import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "lucide-react";
import { format, parseISO } from "date-fns";

interface StartDateFieldProps {
  /** ISO date string `YYYY-MM-DD`. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Optional override of the default hint copy. */
  hint?: string;
}

export function StartDateField({
  value,
  onChange,
  disabled = false,
  hint = "We'll count Day 1 from this date.",
}: StartDateFieldProps) {
  const selectedDate = value ? parseISO(value) : undefined;

  const handleSelect = (date: Date | undefined) => {
    // Required field — ignore "clear" attempts so we never submit blank.
    // The parent already seeded a sensible default on mount.
    if (date) {
      onChange(format(date, "yyyy-MM-dd"));
    }
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">
        Starts<span className="text-destructive ml-1">*</span>
      </Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={disabled}
            className={cn(
              "w-full justify-start text-left font-normal",
              !selectedDate && "text-muted-foreground",
              disabled && "opacity-50 cursor-not-allowed",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {selectedDate ? format(selectedDate, "PPP") : "Pick a start date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

export default StartDateField;
